import { SpanKind, SpanStatusCode } from "@opentelemetry/api";

import { CRITICAL_SPAN_ATTRIBUTE } from "../constants";
import CONV from "../semantic-conventions";
import { ProviderType } from "../types";
import { getFoldedPath } from "../utils";
import { error, log, warn } from "../utils/logger";
import { MAX_SESSION_DURATION_MS, SESSION_END_NAME, SESSION_START_NAME } from "./constants";
import { setCurrentSessionId, setPreviousSessionId } from "./store";
import { SessionConfig, SessionInfo } from "./types";

type SessionLogLevel = "info" | "warn" | "error";

const emitSessionLog = (
  level: SessionLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.session.component": "SessionManager",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }

  if (level === "error") {
    error(payload);
    return;
  }

  log(payload);
};

export class SessionManager {
  private provider: ProviderType;
  private tracer: any;
  private config: SessionConfig;
  private current?: SessionInfo;
  private rolloverTimerId: ReturnType<typeof setTimeout> | null = null;
  private _pageShowListener?: (event: Event) => void;
  private _focusListener?: () => void;
  private _isShuttingDown = false;

  constructor(provider: ProviderType, config?: SessionConfig) {
    this.provider = provider;
    try {
      this.tracer = provider.getTracer("web-tracer-session");
    } catch (err) {
      emitSessionLog(
        "error",
        "[L9RUM Sessions] Failed to acquire tracer",
        {
          "l9rum.session.phase": "constructor",
          "l9rum.session.guard": "get_tracer",
        },
        err,
      );
      this.tracer = provider.getTracer("web-tracer-session");
    }
    this.config = config || {};
  }

  public start() {
    if (this.current?.state === "active") {
      emitSessionLog("warn", "[L9RUM Sessions] start called while session active", {
        "l9rum.session.phase": "start",
        "l9rum.session.guard": "duplicate_start",
        "l9rum.session.current_id": this.current.id,
      });
      return;
    }

    emitSessionLog("info", "[L9RUM Sessions] Starting session", {
      "l9rum.session.phase": "start",
    });
    const previousId = this.current?.id;
    const startSpan = this.tracer.startSpan(SESSION_START_NAME, {
      kind: SpanKind.INTERNAL,
      attributes: { ...this._getPageAttributes(), [CRITICAL_SPAN_ATTRIBUTE]: true },
    });

    const sessionId = startSpan.spanContext().traceId;
    this.current = {
      id: sessionId,
      previousId,
      startedAt: Date.now(),
      state: "active",
    };

    setPreviousSessionId(previousId);
    setCurrentSessionId(sessionId);

    if (previousId) {
      startSpan.setAttribute(CONV.SESSION_PREVIOUS_ID, previousId);
    }

    // Default to OK status
    if (typeof startSpan.setStatus === "function") {
      startSpan.setStatus({ code: SpanStatusCode.OK });
    }

    startSpan.end();
    emitSessionLog("info", "[L9RUM Sessions] Started session", {
      "l9rum.session.phase": "start",
      "l9rum.session.id": sessionId,
      "l9rum.session.previous_id": previousId,
    });

    this._scheduleRollover();
    this._initDocumentEvents();
  }

  public end(reason: "expired" | "exit" = "exit") {
    if (!this.current || this.current.state !== "active") return;

    emitSessionLog("info", "[L9RUM Sessions] Ending session", {
      "l9rum.session.phase": "end",
      "l9rum.session.id": this.current.id,
      "l9rum.session.reason": reason,
    });
    const endSpan = this.tracer.startSpan(SESSION_END_NAME, {
      kind: SpanKind.INTERNAL,
      attributes: {
        ...this._getPageAttributes(),
        [CONV.SESSION_ID]: this.current.id,
        [CRITICAL_SPAN_ATTRIBUTE]: true,
      },
    });

    // Default to OK status
    if (typeof endSpan.setStatus === "function") {
      endSpan.setStatus({ code: SpanStatusCode.OK });
    }

    endSpan.end();

    this.current.state = reason === "expired" ? "expired" : "ended";
    emitSessionLog("info", "[L9RUM Sessions] Ended session", {
      "l9rum.session.phase": "end",
      "l9rum.session.id": this.current.id,
      "l9rum.session.state": this.current.state,
    });

    this._clearRollover();
    setCurrentSessionId(undefined);
  }

  public getSessionId() {
    return this.current?.id;
  }

  public getPreviousSessionId() {
    return this.current?.previousId;
  }

  public rollover() {
    emitSessionLog("info", "[L9RUM Sessions] Rolling over session (max duration reached)", {
      "l9rum.session.phase": "rollover",
    });
    this.end("expired");
    this.start();
    // Notify other subsystems (e.g., view manager) to rotate their state
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("l9rum:session_rollover"));
      }
    } catch (e) {
      emitSessionLog(
        "warn",
        "[L9RUM Sessions] Failed to dispatch session_rollover",
        {
          "l9rum.session.phase": "rollover",
          "l9rum.session.guard": "dispatch_event",
        },
        e,
      );
    }
  }

  private _getPageAttributes() {
    const location = window.location;
    return {
      [CONV.PAGE_URL]: location.href,
      [CONV.PAGE_HASH]: location.hash,
      [CONV.PAGE_ROUTE]: location.pathname,
      [CONV.PAGE_HOSTNAME]: location.hostname,
      [CONV.PAGE_SEARCH]: location.search,
      [CONV.URL_PATH]: location.pathname,
      origin: location.origin,
      path: getFoldedPath(location.pathname),
    };
  }

  private _scheduleRollover() {
    const duration = this._getMaxDurationMs();
    this._scheduleRolloverIn(duration);
  }

  private _clearRollover() {
    if (this.rolloverTimerId) {
      clearTimeout(this.rolloverTimerId);
      this.rolloverTimerId = null;
    }
  }

  private _scheduleRolloverIn(delayMs: number) {
    const safeDelay = Math.max(0, Math.floor(delayMs));
    this._clearRollover();
    this.rolloverTimerId = setTimeout(() => {
      try {
        this.rollover();
      } catch (err) {
        emitSessionLog(
          "error",
          "[L9RUM Sessions] Rollover execution failed",
          {
            "l9rum.session.phase": "rollover",
            "l9rum.session.guard": "timer_execute",
          },
          err,
        );
      }
    }, safeDelay);
    emitSessionLog("info", "[L9RUM Sessions] Scheduled rollover timer", {
      "l9rum.session.phase": "rollover_schedule",
      "l9rum.session.delay_ms": safeDelay,
    });
  }

  private _getMaxDurationMs() {
    return this.config.maxDurationMs ?? MAX_SESSION_DURATION_MS;
  }

  private _checkAndMaybeRollover() {
    if (!this.current) return;
    const now = Date.now();
    const elapsed = now - this.current.startedAt;
    const max = this._getMaxDurationMs();
    if (elapsed >= max) {
      emitSessionLog("info", "[L9RUM Sessions] Max duration exceeded on resume; rolling over", {
        "l9rum.session.phase": "resume_check",
        "l9rum.session.elapsed": elapsed,
        "l9rum.session.max": max,
      });
      this.rollover();
    } else {
      const remaining = max - elapsed;
      emitSessionLog("info", "[L9RUM Sessions] Rescheduling rollover based on remaining time", {
        "l9rum.session.phase": "resume_check",
        "l9rum.session.remaining": remaining,
      });
      this._scheduleRolloverIn(remaining);
    }
  }

  private _initDocumentEvents() {
    if (typeof document === "undefined") return;

    // BFCache restore and general page show
    if (!this._pageShowListener) {
      this._pageShowListener = (event: Event) => {
        const pageShowEvent = event as PageTransitionEvent;
        if (pageShowEvent.persisted) {
          emitSessionLog(
            "info",
            "[L9RUM Sessions] pageshow (BFCache restore) detected; checking session expiry",
            {
              "l9rum.session.phase": "pageshow",
              "l9rum.session.guard": "bfcache",
            },
          );
        } else {
          emitSessionLog("info", "[L9RUM Sessions] pageshow detected; checking session expiry", {
            "l9rum.session.phase": "pageshow",
          });
        }
        this._checkAndMaybeRollover();
      };
      window.addEventListener("pageshow", this._pageShowListener as any);
      emitSessionLog("info", "[L9RUM Sessions] pageshow listener attached", {
        "l9rum.session.phase": "listeners",
        "l9rum.session.guard": "pageshow",
      });
    }

    // Window focus as a general resume signal
    if (!this._focusListener) {
      this._focusListener = () => {
        emitSessionLog("info", "[L9RUM Sessions] window focus detected; checking session expiry", {
          "l9rum.session.phase": "focus",
        });
        this._checkAndMaybeRollover();
      };
      window.addEventListener("focus", this._focusListener as any);
      emitSessionLog("info", "[L9RUM Sessions] focus listener attached", {
        "l9rum.session.phase": "listeners",
        "l9rum.session.guard": "focus",
      });
    }
  }
}
